import { createQueryKeys } from "@lukemorales/query-key-factory";

import { LONG_REQUEST_TIMEOUT_MS } from "../../client";
import type { CoreApiClient } from "../client";
import type {
  MaintenanceLink,
  Module,
  ModuleDefectHistoryEntry,
  ModuleDefectHistoryQuery,
  ModuleStatusDto,
  RawDataPoint,
} from "../types";

export const createModuleQueries = (api: CoreApiClient) => {
  const { client } = api;

  const list = async (buildingId: string): Promise<Module[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/modules`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const maintenanceLink = async (buildingId: string, moduleId: string): Promise<MaintenanceLink> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/modules/{moduleId}/maintenance-link`, {
      params: { path: { buildingId, moduleId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /** Raw BACnet variables of a BMS module, live from the gateway (blocks on the gateway round-trip). */
  const rawData = async (buildingId: string, moduleId: string): Promise<RawDataPoint[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/modules/{moduleId}/raw-data`, {
      params: { path: { buildingId, moduleId } },
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /** Live module status — blocks on the gateway round-trip. */
  const status = async (buildingId: string, moduleId: string): Promise<ModuleStatusDto> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/modules/{moduleId}/status`, {
      params: { path: { buildingId, moduleId } },
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /** GTB faults overlapping a period — a defect with no `end` is still active. */
  const defectHistory = async (
    buildingId: string,
    query: ModuleDefectHistoryQuery,
  ): Promise<ModuleDefectHistoryEntry[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/module-defects`, {
      params: { path: { buildingId }, query },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  return {
    list,
    maintenanceLink,
    rawData,
    status,
    defectHistory,
    queries: createQueryKeys("module", {
      building: (buildingId: string) => ({
        queryKey: [buildingId],
        queryFn: () => list(buildingId),
      }),
      defectHistory: (buildingId: string, query: ModuleDefectHistoryQuery) => ({
        queryKey: [buildingId, query],
        queryFn: () => defectHistory(buildingId, query),
      }),
      maintenanceLink: (buildingId: string, moduleId: string) => ({
        queryKey: [buildingId, moduleId],
        queryFn: () => maintenanceLink(buildingId, moduleId),
      }),
      rawData: (buildingId: string, moduleId: string) => ({
        queryKey: [buildingId, moduleId, "raw-data"],
        queryFn: () => rawData(buildingId, moduleId),
      }),
      status: (buildingId: string, moduleId: string) => ({
        queryKey: [buildingId, moduleId, "status"],
        queryFn: () => status(buildingId, moduleId),
      }),
    }),
  };
};
